import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styled, { keyframes } from 'styled-components';
import { semanticColors, colorTokens } from '@/common/styles/colorTokens';
import { typography } from '@/common/styles/typographyTokens';
import { CompanyUserDto } from '@/services/companyUserService';
import { useBillingStore } from '@/store/useBillingStore';
import { billingService } from '@/services/billingService';
import { toastUtils } from '@/common/utils/toastUtils';
import { CreditCard, Receipt, FileText, ChevronRight, AlertTriangle, ExternalLink, RotateCcw, Info, TrendingUp } from 'lucide-react';
import { RefundRequestItem, BillingPeriod, EntitlementSeverity } from '@/types/billing';
import ModalCenter from '@/common/components/modal/ModalCenter';
import PlanSelectionModal from './PlanSelectionModal';
import PaymentHistorySide from './PaymentHistorySide';
import { useLocation } from 'react-router-dom';

const REFUND_STATUS_LABELS: Record<string, string> = {
  PENDING: '처리 대기',
  APPROVED: '승인',
  REJECTED: '거절',
};

const BILLING_PERIOD_LABELS: Record<BillingPeriod, string> = {
  MONTH: '월',
  QUARTER: '분기',
  SEMI_ANNUAL: '반기',
  YEAR: '년',
};

const REFUND_STATUS_COLORS: Record<string, { bg: string; color: string }> = {
  PENDING: { bg: '#fef9c3', color: '#a16207' },
  APPROVED: { bg: '#dcfce7', color: '#15803d' },
  REJECTED: { bg: '#fee2e2', color: '#dc2626' },
};

const ENTITLEMENT_SEVERITY_STYLES: Record<'INFO' | 'WARNING' | 'CRITICAL', { bg: string; border: string; color: string; iconBg: string }> = {
  INFO: { bg: '#eff6ff', border: '#bfdbfe', color: '#1d4ed8', iconBg: '#dbeafe' },
  WARNING: { bg: '#fff7ed', border: '#fed7aa', color: '#c2410c', iconBg: '#ffedd5' },
  CRITICAL: { bg: '#fef2f2', border: '#fecaca', color: '#b91c1c', iconBg: '#fee2e2' },
};

const getDaysUntil = (dateStr: string): number => {
  const target = new Date(dateStr);
  const now = new Date();
  const diffMs = target.setHours(0, 0, 0, 0) - now.setHours(0, 0, 0, 0);
  return Math.round(diffMs / (1000 * 60 * 60 * 24));
};

const getCapacityLevel = (ratio: number | null): 'normal' | 'near' | 'over' => {
  if (ratio == null) return 'normal';
  if (ratio >= 1) return 'over';
  if (ratio >= 0.8) return 'near';
  return 'normal';
};

interface BillingTabProps {
  company: any;
  companyUser: CompanyUserDto;
}

const BillingTab: React.FC<BillingTabProps> = ({ company, companyUser }) => {
  const {
    billingStatus,
    payments,
    entitlement,
    isLoading,
    loadBillingData,
    loadPayments,
  } = useBillingStore();

  const [isPlanModalOpen, setIsPlanModalOpen] = useState(false);
  const [isHistorySideOpen, setIsHistorySideOpen] = useState(false);
  const [isCancelling, setIsCancelling] = useState(false);
  const [isChangingCard, setIsChangingCard] = useState(false);
  const [isCardChangeConfirmOpen, setIsCardChangeConfirmOpen] = useState(false);
  const [isCardRequiredModalOpen, setIsCardRequiredModalOpen] = useState(false);
  const [refundRequests, setRefundRequests] = useState<RefundRequestItem[]>([]);
  const [isRefundModalOpen, setIsRefundModalOpen] = useState(false);
  const [refundTargetPaymentId, setRefundTargetPaymentId] = useState<number | null>(null);
  const [refundReason, setRefundReason] = useState('');
  const [isSubmittingRefund, setIsSubmittingRefund] = useState(false);
  const cardPopupRef = useRef<Window | null>(null);

  const location = useLocation();

  const activeSubscription = useMemo(
    () => billingStatus?.subscriptions?.find((s) => s.status === 'ACTIVE') ?? null,
    [billingStatus],
  );

  const isManualBilling = activeSubscription?.isManualBilling === true;
  const isPaidPlan = activeSubscription != null && activeSubscription.billingProduct.price > 0;
  const isPaidSubscription = isPaidPlan && !isManualBilling;
  const isConsultationPlan = activeSubscription?.billingProduct.isConsultationRequired === true;

  const paymentRecords = payments?.data ?? [];

  const entitlementBanner = useMemo(() => {
    if (!entitlement || entitlement.reasonCode === 'OK' || entitlement.reasonCode === 'NO_SUBSCRIPTION') {
      return null;
    }

    const { used, limit } = entitlement.capacity;
    const capacityText = `${used}${limit != null ? ` / ${limit}` : ''}개`;
    const cardCtaLabel = billingStatus?.hasBillingKey ? '결제 수단 변경' : '결제 수단 등록';

    switch (entitlement.reasonCode) {
      case 'NEAR_LIMIT':
        return {
          title: '포트폴리오 한도에 근접했습니다',
          description: `현재 ${capacityText}를 관리 중입니다. 한도 초과 전에 플랜 업그레이드를 검토해보세요.`,
          ctaLabel: '플랜 업그레이드',
          ctaAction: 'upgrade' as const,
        };
      case 'OVER_LIMIT':
        return {
          title: '플랜 한도를 초과했습니다',
          description: `현재 ${capacityText}로 한도를 초과하여 신규 포트폴리오 등록이 제한됩니다.`,
          ctaLabel: '플랜 업그레이드',
          ctaAction: 'upgrade' as const,
        };
      case 'PAYMENT_PAST_DUE_GRACE': {
        const dDay = entitlement.payment.graceEndsAt ? getDaysUntil(entitlement.payment.graceEndsAt) : null;
        return {
          title: '결제에 실패했습니다',
          description: dDay != null
            ? `유예기간이 ${Math.max(dDay, 0)}일 남았습니다. 기한 내에 결제 수단을 확인해주세요.`
            : '유예기간 내에 결제 수단을 확인해주세요.',
          ctaLabel: cardCtaLabel,
          ctaAction: 'card' as const,
        };
      }
      case 'PAYMENT_PAST_DUE_BLOCKED':
        return {
          title: '결제 실패로 서비스 이용이 제한되었습니다',
          description: '결제 수단을 재등록하면 이용 제한이 해제됩니다.',
          ctaLabel: cardCtaLabel,
          ctaAction: 'card' as const,
        };
      default:
        return entitlement.message
          ? { title: entitlement.message, description: '', ctaLabel: null, ctaAction: null }
          : null;
    }
  }, [entitlement, billingStatus?.hasBillingKey]);

  const loadRefundRequests = useCallback(async () => {
    try {
      const data = await billingService.getRefundRequests(company.id);
      setRefundRequests(data);
    } catch {
      // silent fail
    }
  }, [company.id]);

  useEffect(() => {
    loadBillingData(company.id);
    loadPayments(company.id);
    loadRefundRequests();
  }, [company.id, loadBillingData, loadPayments, loadRefundRequests]);

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    if (searchParams.get('action') === 'upgrade') {
      setIsPlanModalOpen(true);
    }
  }, [location.search]);

  const handleOpenRefundModal = (paymentRecordId: number) => {
    setRefundTargetPaymentId(paymentRecordId);
    setRefundReason('');
    setIsRefundModalOpen(true);
  };

  const handleSubmitRefund = async () => {
    if (!refundTargetPaymentId || !refundReason.trim()) {
      toastUtils.error('환불 사유를 입력해주세요.');
      return;
    }
    setIsSubmittingRefund(true);
    try {
      const result = await billingService.createRefundRequest(company.id, {
        paymentRecordId: refundTargetPaymentId,
        reason: refundReason.trim(),
      });
      if (result.success) {
        toastUtils.success(result.message || '환불 신청이 접수되었습니다.');
        setIsRefundModalOpen(false);
        await loadRefundRequests();
      } else {
        toastUtils.error(result.message || '환불 신청에 실패했습니다.');
      }
    } catch {
      toastUtils.error('환불 신청 중 오류가 발생했습니다.');
    } finally {
      setIsSubmittingRefund(false);
    }
  };

  const handleCancelSubscription = async () => {
    if (!activeSubscription) return;
    setIsCancelling(true);
    try {
      const result = await billingService.cancelSubscription(company.id, activeSubscription.id);
      if (result.success) {
        toastUtils.success('구독이 해지되었습니다.');
        await loadBillingData(company.id);
      } else {
        toastUtils.error(result.message || '구독 해지에 실패했습니다.');
      }
    } catch {
      toastUtils.error('구독 해지 중 오류가 발생했습니다.');
    } finally {
      setIsCancelling(false);
    }
  };

  const handleRevertCancel = async () => {
    if (!activeSubscription) return;
    setIsCancelling(true);
    try {
      const result = await billingService.revertCancelSubscription(company.id, activeSubscription.id);
      if (result.success) {
        toastUtils.success('구독 해지가 취소되었습니다.');
        await loadBillingData(company.id);
      } else {
        toastUtils.error(result.message || '구독 해지 취소에 실패했습니다.');
      }
    } catch {
      toastUtils.error('구독 해지 취소 중 오류가 발생했습니다.');
    } finally {
      setIsCancelling(false);
    }
  };

  const handleOpenPlanModal = () => {
    if (!billingStatus?.hasBillingKey) {
      setIsCardRequiredModalOpen(true);
      return;
    }
    setIsPlanModalOpen(true);
  };

  const handleCardRequiredRegister = () => {
    setIsCardRequiredModalOpen(false);
    openCardRegisterPopup();
  };

  const openCardRegisterPopup = () => {
    const width = 700;
    const height = 850;
    const left = window.screenX + (window.outerWidth - width) / 2;
    const top = window.screenY + (window.outerHeight - height) / 2;

    const popup = window.open(
      `/billing/card-register?companyId=${company.id}`,
      'cardRegisterPopup',
      `width=${width},height=${height},left=${left},top=${top},resizable=yes,scrollbars=yes`,
    );

    if (!popup) {
      toastUtils.error('팝업이 차단되었습니다. 팝업 차단을 해제해주세요.');
      return;
    }

    cardPopupRef.current = popup;
  };

  const handleChangeCard = () => {
    setIsCardChangeConfirmOpen(true);
  };

  const handleConfirmChangeCard = async () => {
    setIsCardChangeConfirmOpen(false);
    setIsChangingCard(true);
    try {
      const result = await billingService.deleteBillingKey(company.id);
      if (result.success) {
        await loadBillingData(company.id);
        toastUtils.info('기존 카드가 삭제되었습니다. 새 카드를 등록해주세요.');
        openCardRegisterPopup();
      } else {
        toastUtils.error(result.message || '기존 카드 삭제에 실패했습니다.');
      }
    } catch {
      toastUtils.error('카드 변경 중 오류가 발생했습니다.');
    } finally {
      setIsChangingCard(false);
    }
  };

  const handleCardRegisterMessage = useCallback(
    async (event: MessageEvent) => {
      if (event.origin !== window.location.origin) return;
      if (!event.data?.type?.startsWith('CARD_REGISTER_')) return;

      cardPopupRef.current = null;

      if (event.data.type === 'CARD_REGISTER_SUCCESS') {
        toastUtils.success('결제 수단이 등록되었습니다.');
        await loadBillingData(company.id);
      } else if (event.data.type === 'CARD_REGISTER_FAIL') {
        toastUtils.error(event.data.error || '카드 등록에 실패했습니다.');
      } else if (event.data.type === 'CARD_REGISTER_CANCEL') {
        toastUtils.info('카드 등록이 취소되었습니다.');
      }
    },
    [company.id, loadBillingData],
  );

  useEffect(() => {
    window.addEventListener('message', handleCardRegisterMessage);
    return () => window.removeEventListener('message', handleCardRegisterMessage);
  }, [handleCardRegisterMessage]);

  if (isLoading) {
    return (
      <LoadingState>
        <LoadingSpinner />
        <span>결제 정보를 불러오는 중...</span>
      </LoadingState>
    );
  }

  const hasInvestorSpace = company.services?.some((s: any) => s.type === 'INVESTOR');

  return (
    <>
      {/* 결제 수단 + 구독 플랜 - 2열 그리드 */}
      <TwoColumnGrid $columns={hasInvestorSpace ? 2 : 1}>
      <GridColumn>
        <SectionHeader>
          <SectionTitleWrapper>
            <SectionIcon $variant="primary">
              <CreditCard size={20} />
            </SectionIcon>
            <div>
              <SectionTitle>결제 수단</SectionTitle>
              <SectionSubtitle>결제에 사용할 카드를 등록하고 관리하세요</SectionSubtitle>
            </div>
          </SectionTitleWrapper>
        </SectionHeader>

        <BillingCard $delay={0.1}>
          <BillingCardBody>
            {billingStatus?.hasBillingKey ? (
              <CardInfo>
                <CardIconWrapper>
                  <CreditCard size={24} />
                </CardIconWrapper>
                <CardDetails>
                  <CardCompany>{billingStatus.cardCompany ?? '등록된 카드'}</CardCompany>
                  <CardNumber>{billingStatus.cardMaskedNumber ?? '****'}</CardNumber>
                </CardDetails>
                <GhostButton onClick={handleChangeCard} disabled={isChangingCard}>
                  {isChangingCard ? '처리 중...' : '변경'}
                </GhostButton>
              </CardInfo>
            ) : isPaidSubscription ? (
              <CardWarningState>
                <WarningIconWrapper>
                  <AlertTriangle size={24} />
                </WarningIconWrapper>
                <CardWarningContent>
                  <CardWarningTitle>결제 수단이 등록되어 있지 않습니다</CardWarningTitle>
                  <CardWarningText>
                    현재 유료 플랜({activeSubscription?.billingProduct.name})을 구독 중입니다.
                    다음 결제일({activeSubscription?.nextBillingDate?.split('T')[0]})까지 카드를 등록해야 서비스를 정상적으로 이용할 수 있습니다.
                  </CardWarningText>
                  <PrimaryButton onClick={openCardRegisterPopup}>카드 등록하기</PrimaryButton>
                </CardWarningContent>
              </CardWarningState>
            ) : (
              <EmptyState>
                <EmptyText>등록된 결제 수단이 없습니다.</EmptyText>
                <PrimaryButton onClick={openCardRegisterPopup}>카드 등록하기</PrimaryButton>
              </EmptyState>
            )}
          </BillingCardBody>
        </BillingCard>
      </GridColumn>

      {hasInvestorSpace && (
        <GridColumn>
          <SectionHeader>
            <SectionTitleWrapper>
              <SectionIcon $variant="primary">
                <Receipt size={20} />
              </SectionIcon>
              <div>
                <SectionTitle>구독 플랜</SectionTitle>
                <SectionSubtitle>현재 이용 중인 플랜을 관리하세요</SectionSubtitle>
              </div>
            </SectionTitleWrapper>
          </SectionHeader>

          <BillingCard $delay={0.2}>
            <BillingCardHeader>
              <BillingCardTitle>현재 플랜 (투자사)</BillingCardTitle>
              <BadgeGroup>
                {isManualBilling && <ManualBillingBadge>수동 결제 (별도 협의)</ManualBillingBadge>}
                {activeSubscription?.cancelAtPeriodEnd && <CancelScheduledBadge>해지 예정</CancelScheduledBadge>}
                {activeSubscription && !activeSubscription.cancelAtPeriodEnd && <StatusBadge $status="SUCCESS">구독 중</StatusBadge>}
              </BadgeGroup>
            </BillingCardHeader>
            {entitlementBanner && (
              <EntitlementBanner $severity={entitlement!.severity}>
                <EntitlementBannerIcon $severity={entitlement!.severity}>
                  {entitlement!.severity === 'INFO' ? <Info size={18} /> : <AlertTriangle size={18} />}
                </EntitlementBannerIcon>
                <EntitlementBannerContent>
                  <EntitlementBannerTitle>{entitlementBanner.title}</EntitlementBannerTitle>
                  {entitlementBanner.description && (
                    <EntitlementBannerDesc>{entitlementBanner.description}</EntitlementBannerDesc>
                  )}
                </EntitlementBannerContent>
                {entitlementBanner.ctaLabel && (
                  <EntitlementBannerButton
                    $severity={entitlement!.severity}
                    onClick={() => {
                      if (entitlementBanner.ctaAction === 'upgrade') {
                        handleOpenPlanModal();
                      } else if (entitlementBanner.ctaAction === 'card') {
                        billingStatus?.hasBillingKey ? handleChangeCard() : openCardRegisterPopup();
                      }
                    }}
                  >
                    {entitlementBanner.ctaLabel}
                  </EntitlementBannerButton>
                )}
              </EntitlementBanner>
            )}
            <BillingCardBody>
              {activeSubscription ? (
                <PlanInfo>
                  <PlanName>{activeSubscription.billingProduct.name}</PlanName>
                  {activeSubscription.lockedUnitPrice === 0 ? (
                    <PlanPrice>무료 플랜</PlanPrice>
                  ) : (
                    <PlanPrice>
                      {BILLING_PERIOD_LABELS[activeSubscription.billingPeriod ?? 'MONTH']} {activeSubscription.lockedUnitPrice.toLocaleString()}{activeSubscription.billingProduct.priceUnit || '원'}
                    </PlanPrice>
                  )}
                  {entitlement?.capacity && (
                    <CapacityGaugeWrapper>
                      <CapacityGaugeHeader>
                        <CapacityGaugeLabel>
                          <TrendingUp size={14} />
                          포트폴리오 사용량
                        </CapacityGaugeLabel>
                        <CapacityGaugeValue $level={getCapacityLevel(entitlement.capacity.ratio)}>
                          {entitlement.capacity.used}{entitlement.capacity.limit != null ? ` / ${entitlement.capacity.limit}` : ' / 무제한'}
                        </CapacityGaugeValue>
                      </CapacityGaugeHeader>
                      {entitlement.capacity.limit != null && (
                        <CapacityGaugeTrack>
                          <CapacityGaugeFill
                            $level={getCapacityLevel(entitlement.capacity.ratio)}
                            style={{ width: `${Math.min((entitlement.capacity.ratio ?? 0) * 100, 100)}%` }}
                          />
                        </CapacityGaugeTrack>
                      )}
                    </CapacityGaugeWrapper>
                  )}
                  {activeSubscription.lockedUnitPrice > 0 && !isManualBilling && !activeSubscription.cancelAtPeriodEnd && (
                    <NextBillingDate>
                      다음 결제일: {activeSubscription.nextBillingDate.split('T')[0]}
                    </NextBillingDate>
                  )}
                  {isManualBilling && !activeSubscription.cancelAtPeriodEnd && (
                    <NextBillingDate>
                      결제는 별도 협의에 따라 진행됩니다.
                    </NextBillingDate>
                  )}
                  {activeSubscription.cancelAtPeriodEnd && (
                    <CancelScheduledNotice>
                      <AlertTriangle size={16} />
                      <span>
                        {billingStatus?.scheduledSwitchToFreePlanMessage
                          || activeSubscription.switchToFreePlanMessage
                          || `구독이 해지 예정입니다. ${activeSubscription.nextBillingDate.split('T')[0]}에 무료 플랜으로 전환됩니다.`}
                      </span>
                    </CancelScheduledNotice>
                  )}
                  <ButtonGroup>
                    {activeSubscription.cancelAtPeriodEnd ? (
                      <PrimaryButton onClick={handleRevertCancel} disabled={isCancelling}>
                        {isCancelling ? '처리 중...' : '해지 취소'}
                      </PrimaryButton>
                    ) : (
                      <>
                        {!isConsultationPlan && (
                          <PrimaryButton onClick={() => setIsPlanModalOpen(true)}>플랜 변경</PrimaryButton>
                        )}
                        {isPaidPlan && (
                          <SecondaryButton onClick={handleCancelSubscription} disabled={isCancelling}>
                            {isCancelling ? '처리 중...' : '구독 해지'}
                          </SecondaryButton>
                        )}
                      </>
                    )}
                  </ButtonGroup>
                </PlanInfo>
              ) : (
                <EmptyState>
                  <EmptyText>현재 구독 중인 플랜이 없습니다.</EmptyText>
                  <PrimaryButton onClick={handleOpenPlanModal}>플랜 선택하기</PrimaryButton>
                </EmptyState>
              )}
            </BillingCardBody>
          </BillingCard>
        </GridColumn>
      )}
      </TwoColumnGrid>

      {/* 최근 결제 내역 섹션 - 투자사 서비스 활성 시에만 */}
      {hasInvestorSpace && (
        <Section>
          <SectionHeader>
            <SectionTitleWrapper>
              <SectionIcon $variant="success">
                <FileText size={20} />
              </SectionIcon>
              <div>
                <SectionTitle>최근 결제 내역</SectionTitle>
                <SectionSubtitle>최근 결제된 내역을 확인하세요</SectionSubtitle>
              </div>
            </SectionTitleWrapper>
            <TextLink onClick={() => setIsHistorySideOpen(true)}>
              전체 보기 <ChevronRight size={16} />
            </TextLink>
          </SectionHeader>
          
          <HistoryContainer>
            {paymentRecords.length > 0 ? (
              <HistoryList>
                {paymentRecords.slice(0, 3).map((item) => {
                  const isRefunded = !!item.refundedAt;

                  return isRefunded ? (
                    <HistoryItemRefunded key={item.id}>
                      <HistoryDate>{item.refundedAt!.split('T')[0]}</HistoryDate>
                      <HistoryDescGroup>
                        <RefundedDesc>
                          <RotateCcw size={13} />
                          환불 완료
                        </RefundedDesc>
                        <HistoryCardInfo>
                          {item.title}
                          {item.refundReason && ` · ${item.refundReason}`}
                        </HistoryCardInfo>
                      </HistoryDescGroup>
                      <RefundedAmount>-{(item.refundedAmount ?? item.amount).toLocaleString()}원</RefundedAmount>
                      <HistoryActions>
                        <StatusBadge $status="REFUNDED">환불</StatusBadge>
                        {item.receiptUrl && (
                          <ReceiptLink href={item.receiptUrl} target="_blank" rel="noopener noreferrer">
                            <ExternalLink size={13} />
                            영수증
                          </ReceiptLink>
                        )}
                      </HistoryActions>
                    </HistoryItemRefunded>
                  ) : (
                    <HistoryItem key={item.id}>
                      <HistoryDate>{(item.paidAt ?? item.createdAt).split('T')[0]}</HistoryDate>
                      <HistoryDescGroup>
                        <HistoryDesc>{item.title}</HistoryDesc>
                        {item.cardCompany && (
                          <HistoryCardInfo>{item.cardCompany} {item.cardMaskedNumber}</HistoryCardInfo>
                        )}
                      </HistoryDescGroup>
                      <HistoryAmount>{item.amount.toLocaleString()}원</HistoryAmount>
                      <HistoryActions>
                        <StatusBadge $status={item.status}>
                          {item.status === 'SUCCESS' ? '성공' : '실패'}
                        </StatusBadge>
                        {item.receiptUrl && (
                          <ReceiptLink href={item.receiptUrl} target="_blank" rel="noopener noreferrer">
                            <ExternalLink size={13} />
                            영수증
                          </ReceiptLink>
                        )}
                        {item.status === 'SUCCESS' && !refundRequests.some(r => r.paymentRecordId === item.id) && (
                          <RefundButton onClick={() => handleOpenRefundModal(item.id)}>
                            환불 신청
                          </RefundButton>
                        )}
                      </HistoryActions>
                    </HistoryItem>
                  );
                })}
              </HistoryList>
            ) : (
              <HistoryEmpty>결제 내역이 없습니다.</HistoryEmpty>
            )}
          </HistoryContainer>
        </Section>
      )}

      {/* 환불 신청 목록 */}
      {hasInvestorSpace && refundRequests.length > 0 && (
        <Section>
          <SectionHeader>
            <SectionTitleWrapper>
              <SectionIcon $variant="warning">
                <RotateCcw size={20} />
              </SectionIcon>
              <div>
                <SectionTitle>환불 신청 내역</SectionTitle>
                <SectionSubtitle>환불 신청 상태를 확인하세요</SectionSubtitle>
              </div>
            </SectionTitleWrapper>
          </SectionHeader>
          <RefundListContainer>
            {refundRequests.map((req) => (
              <RefundItem key={req.id}>
                <RefundDate>{req.createdAt.split('T')[0]}</RefundDate>
                <RefundDescGroup>
                  <RefundReason>{req.reason}</RefundReason>
                  {req.rejectReason && (
                    <RefundRejectReason>거절 사유: {req.rejectReason}</RefundRejectReason>
                  )}
                </RefundDescGroup>
                <RefundAmount>
                  {req.requestedAmount != null ? `${req.requestedAmount.toLocaleString()}원` : ''}
                </RefundAmount>
                <RefundStatusBadge $status={req.status}>
                  {REFUND_STATUS_LABELS[req.status] ?? req.status}
                </RefundStatusBadge>
              </RefundItem>
            ))}
          </RefundListContainer>
        </Section>
      )}

      {/* 모달 및 사이드 패널 */}
      {hasInvestorSpace && isPlanModalOpen && (
        <PlanSelectionModal 
          isOpen={isPlanModalOpen} 
          onClose={() => setIsPlanModalOpen(false)}
          currentProductId={activeSubscription?.billingProduct.id}
          recommendedProductId={entitlement?.recommendedPlanId ?? undefined}
          companyId={company.id}
          mode={activeSubscription ? 'change' : 'create'}
          subscriptionId={activeSubscription?.id}
          onCardRegisterRequest={() => {
            openCardRegisterPopup();
          }}
          onComplete={() => {
            loadPayments(company.id);
            loadRefundRequests();
          }}
        />
      )}

      {hasInvestorSpace && (
        <PaymentHistorySide
          isOpen={isHistorySideOpen}
          onClose={() => setIsHistorySideOpen(false)}
          companyId={company.id}
          refundRequests={refundRequests}
          onRefundRequest={handleOpenRefundModal}
        />
      )}

      {/* 카드 변경 확인 모달 */}
      <ModalCenter
        isOpen={isCardChangeConfirmOpen}
        onClose={() => setIsCardChangeConfirmOpen(false)}
        title="카드 변경"
        maxWidth="440px"
        minWidth="400px"
        footer={
          <ConfirmButtonGroup>
            <ConfirmCancelButton onClick={() => setIsCardChangeConfirmOpen(false)}>
              취소
            </ConfirmCancelButton>
            <ConfirmActionButton onClick={handleConfirmChangeCard}>
              카드 변경하기
            </ConfirmActionButton>
          </ConfirmButtonGroup>
        }
      >
        <ConfirmBody>
          <ConfirmIconWrapper>
            <AlertTriangle size={28} />
          </ConfirmIconWrapper>
          <ConfirmText>
            기존 카드를 삭제한 후 새 카드를 등록해야 합니다.
          </ConfirmText>
          <ConfirmWarning>
            카드 등록을 완료하지 않으면 결제 수단이 없는 상태가 됩니다.
          </ConfirmWarning>
        </ConfirmBody>
      </ModalCenter>

      {/* 결제 수단 등록 안내 모달 */}
      <ModalCenter
        isOpen={isCardRequiredModalOpen}
        onClose={() => setIsCardRequiredModalOpen(false)}
        title="결제 수단 등록 필요"
        maxWidth="440px"
        minWidth="400px"
        footer={
          <ConfirmButtonGroup>
            <ConfirmCancelButton onClick={() => setIsCardRequiredModalOpen(false)}>
              취소
            </ConfirmCancelButton>
            <CardRequiredActionButton onClick={handleCardRequiredRegister}>
              <CreditCard size={16} />
              카드 등록하기
            </CardRequiredActionButton>
          </ConfirmButtonGroup>
        }
      >
        <ConfirmBody>
          <CardRequiredIconWrapper>
            <CreditCard size={28} />
          </CardRequiredIconWrapper>
          <ConfirmText>
            플랜을 선택하기 전에 결제 수단을 먼저 등록해주세요.
          </ConfirmText>
          <ConfirmWarning>
            카드 등록 후 원하시는 플랜을 선택하실 수 있습니다.
          </ConfirmWarning>
        </ConfirmBody>
      </ModalCenter>

      {/* 환불 신청 모달 */}
      <ModalCenter
        isOpen={isRefundModalOpen}
        onClose={() => setIsRefundModalOpen(false)}
        title="환불 신청"
        maxWidth="480px"
        minWidth="420px"
        footer={
          <ConfirmButtonGroup>
            <ConfirmCancelButton onClick={() => setIsRefundModalOpen(false)}>
              취소
            </ConfirmCancelButton>
            <ConfirmActionButton onClick={handleSubmitRefund} disabled={isSubmittingRefund}>
              {isSubmittingRefund ? '처리 중...' : '환불 신청'}
            </ConfirmActionButton>
          </ConfirmButtonGroup>
        }
      >
        <RefundModalBody>
          <RefundFormGroup>
            <RefundLabel>환불 사유 <RequiredMark>*</RequiredMark></RefundLabel>
            <RefundTextarea
              placeholder="환불 사유를 입력해주세요"
              value={refundReason}
              onChange={(e) => setRefundReason(e.target.value)}
              rows={4}
            />
          </RefundFormGroup>
        </RefundModalBody>
      </ModalCenter>
    </>
  );
};

// ============================================
// Animations
// ============================================

const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const spin = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

// ============================================
// Styled Components
// ============================================

const Section = styled.section`
  margin-bottom: 2rem;
  animation: ${fadeInUp} 0.6s ease-out 0.1s both;
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const TwoColumnGrid = styled.div<{ $columns: 1 | 2 }>`
  display: grid;
  grid-template-columns: repeat(${props => props.$columns}, 1fr);
  gap: 1.5rem;
  margin-bottom: 2rem;
  align-items: stretch;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

const GridColumn = styled.div`
  display: flex;
  flex-direction: column;
  animation: ${fadeInUp} 0.6s ease-out 0.1s both;
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 1.25rem;
`;

const SectionTitleWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
`;

const SectionIcon = styled.div<{ $variant: 'primary' | 'success' | 'warning' }>`
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 10px;
  
  ${props => props.$variant === 'primary' && `
    background: ${semanticColors.background.tertiary};
    color: ${semanticColors.text.brand};
  `}
  
  ${props => props.$variant === 'success' && `
    background: ${semanticColors.background.success.secondary};
    color: ${semanticColors.text.onSuccess.primary};
  `}
  
  ${props => props.$variant === 'warning' && `
    background: ${semanticColors.background.warning.secondary};
    color: ${semanticColors.text.onWarning.primary};
  `}
`;

const SectionTitle = styled.h2`
  ${typography.subtitle.subtitle1}
  color: ${semanticColors.text.primary};
`;

const SectionSubtitle = styled.p`
  ${typography.caption.caption2}
  color: ${semanticColors.text.tertiary};
  margin-top: 0.125rem;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
  gap: 1.25rem;
`;

const BillingCard = styled.div<{ $delay: number }>`
  display: flex;
  flex-direction: column;
  flex: 1;
  padding: 1.5rem;
  background: ${semanticColors.background.secondary};
  border: 1px solid ${semanticColors.border.primary};
  border-radius: 20px;
  transition: all 0.25s ease;
  animation: ${fadeIn} 0.4s ease-out ${props => props.$delay}s both;
  
  &:hover {
    border-color: ${semanticColors.border.secondary};
    box-shadow: 0 8px 24px rgba(0,0,0,0.06);
  }
`;

const BadgeGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const ManualBillingBadge = styled.div`
  display: inline-flex;
  align-items: center;
  padding: 0.25rem 0.625rem;
  border-radius: 100px;
  ${typography.caption.caption2};
  font-weight: 600;
  background: #ede9fe;
  color: #7c3aed;
`;

const CancelScheduledBadge = styled.div`
  display: inline-flex;
  align-items: center;
  padding: 0.25rem 0.625rem;
  border-radius: 100px;
  ${typography.caption.caption2};
  font-weight: 600;
  background: #fee2e2;
  color: #dc2626;
`;

const CancelScheduledNotice = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
  padding: 0.875rem 1rem;
  border-radius: 10px;
  background: #fef2f2;
  border: 1px solid #fecaca;
  color: #b91c1c;
  ${typography.paragraph.p2.default};
  line-height: 1.5;

  svg {
    flex-shrink: 0;
    margin-top: 0.125rem;
  }
`;

const EntitlementBanner = styled.div<{ $severity: EntitlementSeverity }>`
  display: flex;
  align-items: flex-start;
  gap: 0.875rem;
  padding: 1rem 1.25rem;
  margin: 0 0 1.25rem;
  border-radius: 14px;
  background: ${p => ENTITLEMENT_SEVERITY_STYLES[p.$severity as 'INFO' | 'WARNING' | 'CRITICAL']?.bg ?? '#f3f4f6'};
  border: 1px solid ${p => ENTITLEMENT_SEVERITY_STYLES[p.$severity as 'INFO' | 'WARNING' | 'CRITICAL']?.border ?? '#e5e7eb'};
`;

const EntitlementBannerIcon = styled.div<{ $severity: EntitlementSeverity }>`
  flex-shrink: 0;
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 10px;
  background: ${p => ENTITLEMENT_SEVERITY_STYLES[p.$severity as 'INFO' | 'WARNING' | 'CRITICAL']?.iconBg ?? '#e5e7eb'};
  color: ${p => ENTITLEMENT_SEVERITY_STYLES[p.$severity as 'INFO' | 'WARNING' | 'CRITICAL']?.color ?? '#6b7280'};
`;

const EntitlementBannerContent = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  min-width: 0;
`;

const EntitlementBannerTitle = styled.div`
  ${typography.paragraph.p2.default};
  font-weight: 700;
  color: ${semanticColors.text.primary};
`;

const EntitlementBannerDesc = styled.div`
  ${typography.caption.caption1.default};
  color: ${semanticColors.text.secondary};
  line-height: 1.5;
`;

const EntitlementBannerButton = styled.button<{ $severity: EntitlementSeverity }>`
  flex-shrink: 0;
  align-self: center;
  padding: 0.5rem 0.875rem;
  border-radius: 8px;
  border: none;
  ${typography.caption.caption1.default};
  font-weight: 700;
  cursor: pointer;
  white-space: nowrap;
  transition: opacity 0.2s ease;
  background: ${p => ENTITLEMENT_SEVERITY_STYLES[p.$severity as 'INFO' | 'WARNING' | 'CRITICAL']?.color ?? '#6b7280'};
  color: white;

  &:hover {
    opacity: 0.85;
  }
`;

const CapacityGaugeWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const CapacityGaugeHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const CapacityGaugeLabel = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  ${typography.caption.caption2};
  color: ${semanticColors.text.tertiary};
`;

const CAPACITY_LEVEL_COLORS: Record<'normal' | 'near' | 'over', string> = {
  normal: colorTokens.brand[500],
  near: '#f59e0b',
  over: '#dc2626',
};

const CapacityGaugeValue = styled.span<{ $level: 'normal' | 'near' | 'over' }>`
  ${typography.caption.caption1.default};
  font-weight: 700;
  color: ${p => CAPACITY_LEVEL_COLORS[p.$level]};
`;

const CapacityGaugeTrack = styled.div`
  width: 100%;
  height: 8px;
  border-radius: 100px;
  background: ${semanticColors.background.tertiary};
  overflow: hidden;
`;

const CapacityGaugeFill = styled.div<{ $level: 'normal' | 'near' | 'over' }>`
  height: 100%;
  border-radius: 100px;
  background: ${p => CAPACITY_LEVEL_COLORS[p.$level]};
  transition: width 0.3s ease;
`;

const BillingCardHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 1.25rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid ${semanticColors.border.primary};
`;

const BillingCardTitle = styled.h3`
  ${typography.subtitle.subtitle1}
  color: ${semanticColors.text.primary};
`;

const BillingCardBody = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
`;

const PlanInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

const PlanName = styled.div`
  ${typography.heading.h5};
  color: ${semanticColors.text.primary};
`;

const PlanPrice = styled.div`
  ${typography.heading.h6};
  color: ${semanticColors.text.primary};
  display: flex;
  align-items: baseline;
  gap: 0.5rem;
`;

const PlanCycle = styled.span`
  ${typography.paragraph.p2.default};
  color: ${semanticColors.text.secondary};
`;

const NextBillingDate = styled.div`
  ${typography.paragraph.p2.default};
  color: ${semanticColors.text.secondary};
  margin-bottom: 1rem;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 0.75rem;
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 2rem 0;
  gap: 1rem;
  text-align: center;
  flex: 1;
`;

const EmptyText = styled.div`
  ${typography.paragraph.p2.default};
  color: ${semanticColors.text.secondary};
`;

const CardWarningState = styled.div`
  display: flex;
  gap: 1.25rem;
  padding: 1.5rem;
  background: #fef3c7;
  border: 1px solid #fcd34d;
  border-radius: 14px;
`;

const WarningIconWrapper = styled.div`
  flex-shrink: 0;
  width: 44px;
  height: 44px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #fbbf24;
  color: #92400e;
  border-radius: 12px;
`;

const CardWarningContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const CardWarningTitle = styled.div`
  ${typography.subtitle.subtitle1};
  color: #92400e;
  font-weight: 700;
`;

const CardWarningText = styled.div`
  ${typography.paragraph.p2.default};
  color: #a16207;
  line-height: 1.6;
  margin-bottom: 0.5rem;
`;

const ButtonBase = styled.button`
  padding: 0.75rem 1.25rem;
  border-radius: 10px;
  ${typography.paragraph.p2.default};
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  display: inline-flex;
  align-items: center;
  justify-content: center;
`;

const PrimaryButton = styled(ButtonBase)`
  background: linear-gradient(135deg, #059669 0%, #10b981 100%);
  color: white;
  border: none;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 16px rgba(16, 185, 129, 0.3);
  }
`;

const SecondaryButton = styled(ButtonBase)`
  background: ${semanticColors.background.secondary};
  color: ${semanticColors.text.secondary};
  border: 1px solid ${semanticColors.border.primary};
  
  &:hover {
    background: ${semanticColors.background.tertiary};
    color: ${semanticColors.text.primary};
  }
`;

const GhostButton = styled(ButtonBase)`
  background: transparent;
  color: ${colorTokens.brand[600]};
  border: none;
  padding: 0.5rem 1rem;
  
  &:hover {
    background: ${semanticColors.background.tertiary};
  }
`;

const CardInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1.25rem;
  background: ${semanticColors.background.primary};
  border: 1px solid ${semanticColors.border.primary};
  border-radius: 12px;
`;

const CardIconWrapper = styled.div`
  width: 48px;
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, rgba(59, 130, 246, 0.15) 0%, rgba(96, 165, 250, 0.2) 100%);
  color: #3b82f6;
  border-radius: 12px;
`;

const CardDetails = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
`;

const CardCompany = styled.span`
  ${typography.paragraph.p2.default};
  color: ${semanticColors.text.primary};
  font-weight: 600;
`;

const CardNumber = styled.span`
  ${typography.caption.caption1.default};
  color: ${semanticColors.text.tertiary};
`;

const HistoryContainer = styled.div`
  background: ${semanticColors.background.secondary};
  border: 1px solid ${semanticColors.border.primary};
  border-radius: 20px;
  overflow: hidden;
  animation: ${fadeIn} 0.4s ease-out 0.3s both;
`;

const TextLink = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  background: none;
  border: none;
  color: ${semanticColors.text.secondary};
  ${typography.paragraph.p2.default};
  cursor: pointer;
  padding: 0.5rem;
  border-radius: 8px;
  transition: all 0.2s ease;

  &:hover {
    color: ${semanticColors.text.primary};
    background: ${semanticColors.background.tertiary};
  }
`;

const HistoryList = styled.div`
  display: flex;
  flex-direction: column;
`;

const HistoryItem = styled.div`
  display: grid;
  grid-template-columns: 110px 1fr 130px auto;
  align-items: center;
  padding: 1.25rem 1.5rem;
  border-bottom: 1px solid ${semanticColors.border.primary};
  transition: background 0.2s ease;

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background: ${semanticColors.background.tertiary};
  }
`;

const HistoryItemRefunded = styled(HistoryItem)`
  background: #faf5ff;
  opacity: 0.85;

  &:hover {
    background: #f3e8ff;
  }
`;

const RefundedDesc = styled.div`
  display: flex;
  align-items: center;
  gap: 0.375rem;
  ${typography.paragraph.p2.default};
  color: #7c3aed;
  font-weight: 600;
`;

const RefundedAmount = styled.div`
  ${typography.paragraph.p2.default};
  color: #7c3aed;
  font-weight: 600;
  text-align: right;
  padding-right: 1.5rem;
`;

const HistoryDate = styled.div`
  ${typography.paragraph.p2.default};
  color: ${semanticColors.text.secondary};
`;

const HistoryDescGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.125rem;
`;

const HistoryDesc = styled.div`
  ${typography.paragraph.p2.default};
  color: ${semanticColors.text.primary};
  font-weight: 500;
`;

const HistoryCardInfo = styled.div`
  ${typography.caption.caption2};
  color: ${semanticColors.text.tertiary};
`;

const HistoryActions = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const ReceiptLink = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  ${typography.caption.caption2};
  color: ${colorTokens.brand[600]};
  text-decoration: none;
  padding: 0.25rem 0.5rem;
  border-radius: 6px;
  transition: all 0.2s ease;

  &:hover {
    background: ${semanticColors.background.tertiary};
    text-decoration: underline;
  }
`;

const HistoryAmount = styled.div`
  ${typography.paragraph.p2.default};
  color: ${semanticColors.text.primary};
  font-weight: 600;
  text-align: right;
  padding-right: 1.5rem;
`;

const StatusBadge = styled.div<{ $status: string }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0.25rem 0.625rem;
  border-radius: 100px;
  ${typography.caption.caption2};
  font-weight: 600;
  
  background: ${props => {
    switch (props.$status) {
      case 'SUCCESS': return semanticColors.background.success.secondary;
      case 'FAILED': return semanticColors.background.danger.secondary;
      case 'REFUNDED': return '#ede9fe';
      default: return semanticColors.background.warning.secondary;
    }
  }};
  
  color: ${props => {
    switch (props.$status) {
      case 'SUCCESS': return semanticColors.text.onSuccess.primary;
      case 'FAILED': return semanticColors.text.onDanger.secondary;
      case 'REFUNDED': return '#7c3aed';
      default: return semanticColors.text.onWarning.primary;
    }
  }};
`;

const HistoryEmpty = styled.div`
  padding: 3rem;
  text-align: center;
  color: ${semanticColors.text.tertiary};
  ${typography.paragraph.p2.default};
`;

const LoadingState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 1rem;
  padding: 4rem 2rem;
  ${typography.paragraph.p2.default}
  color: ${semanticColors.text.tertiary};
`;

const LoadingSpinner = styled.div`
  width: 24px;
  height: 24px;
  border: 3px solid ${semanticColors.border.primary};
  border-top-color: ${semanticColors.background.brand};
  border-radius: 50%;
  animation: ${spin} 0.8s linear infinite;
`;

// 카드 변경 확인 모달
const ConfirmBody = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
  padding: 2rem 1.5rem;
  text-align: center;
`;

const ConfirmIconWrapper = styled.div`
  width: 56px;
  height: 56px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #fef3c7;
  color: #d97706;
  border-radius: 50%;
`;

const ConfirmText = styled.p`
  ${typography.paragraph.p1.default};
  color: ${semanticColors.text.primary};
  margin: 0;
  line-height: 1.6;
`;

const ConfirmWarning = styled.p`
  ${typography.paragraph.p2.default};
  color: #b45309;
  background: #fffbeb;
  border: 1px solid #fde68a;
  border-radius: 10px;
  padding: 0.75rem 1rem;
  margin: 0;
  line-height: 1.5;
`;

const ConfirmButtonGroup = styled.div`
  display: flex;
  gap: 0.75rem;
  width: 100%;
`;

const CardRequiredIconWrapper = styled.div`
  width: 56px;
  height: 56px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #dbeafe;
  color: ${colorTokens.brand[600]};
  border-radius: 50%;
`;

const CardRequiredActionButton = styled.button`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 0.75rem;
  border-radius: 10px;
  border: none;
  background: ${colorTokens.brand[600]};
  color: white;
  ${typography.paragraph.p2.default};
  font-weight: 700;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: ${colorTokens.brand[700]};
  }
`;

const ConfirmCancelButton = styled.button`
  flex: 1;
  padding: 0.75rem;
  border-radius: 10px;
  border: 1px solid ${semanticColors.border.primary};
  background: ${semanticColors.background.secondary};
  color: ${semanticColors.text.secondary};
  ${typography.paragraph.p2.default};
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: ${semanticColors.background.tertiary};
    color: ${semanticColors.text.primary};
  }
`;

const ConfirmActionButton = styled.button`
  flex: 1;
  padding: 0.75rem;
  border-radius: 10px;
  border: none;
  background: #d97706;
  color: white;
  ${typography.paragraph.p2.default};
  font-weight: 700;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: #b45309;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const RefundButton = styled.button`
  ${typography.caption.caption2};
  color: ${semanticColors.text.tertiary};
  background: none;
  border: 1px solid ${semanticColors.border.primary};
  border-radius: 6px;
  padding: 0.2rem 0.5rem;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    color: #dc2626;
    border-color: #fca5a5;
    background: #fef2f2;
  }
`;

const RefundListContainer = styled.div`
  display: flex;
  flex-direction: column;
  background: ${semanticColors.background.secondary};
  border: 1px solid ${semanticColors.border.primary};
  border-radius: 20px;
  overflow: hidden;
  animation: ${fadeIn} 0.4s ease-out 0.3s both;
`;

const RefundItem = styled.div`
  display: grid;
  grid-template-columns: 110px 1fr 130px auto;
  align-items: center;
  padding: 1.25rem 1.5rem;
  border-bottom: 1px solid ${semanticColors.border.primary};
  transition: background 0.2s ease;

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background: ${semanticColors.background.tertiary};
  }
`;

const RefundDate = styled.div`
  ${typography.paragraph.p2.default};
  color: ${semanticColors.text.secondary};
`;

const RefundDescGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.125rem;
`;

const RefundReason = styled.div`
  ${typography.paragraph.p2.default};
  color: ${semanticColors.text.primary};
  font-weight: 500;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const RefundRejectReason = styled.div`
  ${typography.caption.caption2};
  color: #dc2626;
`;

const RefundAmount = styled.div`
  ${typography.paragraph.p2.default};
  color: ${semanticColors.text.primary};
  font-weight: 600;
  text-align: right;
  padding-right: 1.5rem;
`;

const RefundStatusBadge = styled.div<{ $status: string }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0.25rem 0.625rem;
  border-radius: 100px;
  ${typography.caption.caption2};
  font-weight: 600;
  background: ${(p) => REFUND_STATUS_COLORS[p.$status]?.bg ?? '#f3f4f6'};
  color: ${(p) => REFUND_STATUS_COLORS[p.$status]?.color ?? '#6b7280'};
`;

const RefundModalBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
  padding: 1.5rem;
`;

const RefundFormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const RefundLabel = styled.label`
  ${typography.paragraph.p2.default};
  color: ${semanticColors.text.primary};
  font-weight: 600;
`;

const RequiredMark = styled.span`
  color: #dc2626;
`;

const RefundTextarea = styled.textarea`
  padding: 0.75rem 1rem;
  border: 1px solid ${semanticColors.border.primary};
  border-radius: 10px;
  ${typography.paragraph.p2.default};
  color: ${semanticColors.text.primary};
  background: ${semanticColors.background.secondary};
  resize: vertical;
  transition: border-color 0.2s;

  &:focus {
    outline: none;
    border-color: ${colorTokens.brand[500]};
  }

  &::placeholder {
    color: ${semanticColors.text.tertiary};
  }
`;

export default BillingTab;
